import { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  useUpdateFavouritesMutation,
  useUpdateLikesMutation,
} from '../../../../forYou/mediaSlidersApiSlice';
import { AuthContext } from '../../../context/auth-context';
import { SlideContent } from '../types';

export function useSliderTooltip(
  slideContent: SlideContent,
  closeTooltip: () => void,
  handleTileTooltip: (e: React.MouseEvent) => void
) {
  const auth = useContext(AuthContext);
  const [updateFavourites] = useUpdateFavouritesMutation();
  const [updateLikes] = useUpdateLikesMutation();

  const [favourite, setFavourite] = useState<boolean | undefined>();
  const [like, setLike] = useState<boolean | undefined>();
  const [dislike, setDislike] = useState<boolean | undefined>();

  const favourites = useSelector((state: any) => state.mediaSliders.favourites);
  const likes = useSelector((state: any) => state.mediaSliders.likes);
  const dislikes = useSelector((state: any) => state.mediaSliders.dislikes);

  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!slideContent || !favourites || !favourites?.media || !likes || !dislikes) return;
    setFavourite(() => favourites.media.some((element: any) => element._id === slideContent._id));
    setLike(() => likes.includes(slideContent._id));
    setDislike(() => dislikes.includes(slideContent._id));
  }, [favourites?.media?.length, likes, dislikes, slideContent, favourites]);

  useEffect(() => {
    const focusableElements = tooltipRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements?.length) return;

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      } else if (e.key === 'Escape') {
        closeTooltip();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        (document.activeElement as HTMLElement)?.click();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    firstElement.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeTooltip]);

  const handleTooltipClick = (e: React.SyntheticEvent | Event, type: string) => {
    e.preventDefault();
    handleTileTooltip(e as React.MouseEvent);

    let url = '/media';
    if (slideContent?.category?.toLowerCase() === 'programme') {
      url = '/programme';
    } else if (slideContent?.category?.toLowerCase() === 'masterclass') {
      url = '/masterclass';
    } else if (slideContent?.chid.startsWith('TA')) {
      url = '/take-actions';
    }

    if (type === 'fave' && slideContent?._id) {
      updateFavourites({
        token: auth.token,
        url: `${url}/favourites`,
        mediaId: slideContent._id,
      }).catch(console.error);
    } else if ((type === 'like' || type === 'dislike') && slideContent) {
      updateLikes({
        token: auth.token,
        url: `${url}/preference`,
        action: type,
        mediaId: slideContent._id,
      }).catch(console.error);
    }

    closeTooltip();
  };

  return {
    favourite,
    tooltipRef,
    handleTooltipClick,
  };
}
